/**
 * <photo-cropper data-aspect-ratio="2/3"
 *                data-target-px-width="2480"
 *                data-target-px-height="3508">
 *   <input type="file" accept="image/jpeg,image/png" data-photo-cropper-file hidden>
 *   <div data-photo-cropper-drop role="button" tabindex="0">… drop UI …</div>
 *   <div data-photo-cropper-crop hidden>
 *     <canvas data-photo-cropper-source></canvas>
 *   </div>
 *   <div data-photo-cropper-banner hidden></div>
 * </photo-cropper>
 *
 * Upload + crop + DPI + JPEG export. The exported JPEG can be composited with a
 * filter, border ("Kant") and headline/subtitle caption — see exportToFile(opts).
 * Rotation is baked into the source canvas, so all crop math + export work in the
 * rotated frame; crop coordinates are in SOURCE-CANVAS pixels (== natural res).
 */
(function () {
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  // No upload is ever rejected for resolution. Below this floor (A4 portrait @ 150 DPI,
  // either orientation) meetsFloor:false rides the loaded event, and the host shows the
  // AI-enhancement opt-in card (properties[_ai_enhance]) instead of an error.
  const MIN_NATURAL_W = 1240;
  const MIN_NATURAL_H = 1754;
  // Fallbacks only. The host normally drives these per poster size via data-dpi-floor /
  // data-dpi-ok, because the acceptable DPI depends on both the print size (viewing
  // distance) and whether print prep upscales the original. See QUALITY_SIZES in
  // assets/main-product-custom-photo.js.
  const DPI_RED_BELOW = 150;
  const DPI_YELLOW_BELOW = 250;

  // Border ("Kant") mat colors + geometry.
  const BORDER_FRAC = 0.05;                 // uniform inset as fraction of the short edge
  const CAPTION_BAND_FRAC = 0.12;           // extra bottom mat height for the caption (bordered)
  // Beige is the Nordic warm-grey beige (#E6DED4), not the yellower #ece7dd it started as.
  // Keep in sync with the .cp-swatch inline colour in sections/main-product-custom-photo.liquid.
  const MAT_COLORS = { white: '#ffffff', beige: '#E6DED4', black: '#111111' };

  // Caption font stacks (web-safe so the canvas bake is guaranteed to render them).
  const FONT_FAMILIES = {
    modern:     '"Helvetica Neue", Arial, sans-serif',
    classic:    'Georgia, "Times New Roman", serif',
    minimalist: '"Helvetica Neue", Arial, sans-serif'
  };
  const FONT_WEIGHTS = { modern: '600', classic: '500', minimalist: '300' };

  function getCustomPhotoString(key, vars) {
    const strings = window.theme && window.theme.strings && window.theme.strings.customPhoto;
    let s = strings && strings[key];
    if (!s) return null;
    if (vars) {
      Object.keys(vars).forEach(k => { s = s.replace('{{ ' + k + ' }}', vars[k]); });
    }
    return s;
  }

  function cssFilterFor(key) {
    if (window.PosteraPosterFilters) return window.PosteraPosterFilters.cssFilter(key);
    return (!key || key === 'original') ? 'none' : 'none';
  }

  class PhotoCropper extends HTMLElement {
    static get observedAttributes() { return ['data-aspect-ratio', 'data-print-cm-width', 'data-print-cm-height', 'data-dpi-ok', 'data-dpi-floor']; }

    connectedCallback() {
      if (this._wiredUp) return;
      this._wiredUp = true;
      this.fileInput = this.querySelector('[data-photo-cropper-file]');
      this.dropZone  = this.querySelector('[data-photo-cropper-drop]');
      this.cropArea  = this.querySelector('[data-photo-cropper-crop]');
      this.sourceCanvas = this.querySelector('[data-photo-cropper-source]');
      this.banner = this.querySelector('[data-photo-cropper-banner]');
      this.statusEl = document.querySelector('[data-photo-cropper-status]') || null;
      this.statusDotEl = document.querySelector('[data-photo-cropper-status-dot]') || null;
      this.statusTextEl = document.querySelector('[data-photo-cropper-status-text]') || null;
      this.cropWindow = this.querySelector('[data-photo-cropper-window]');
      this.dim = this.querySelector('[data-photo-cropper-dim]');
      this.handles = Array.from(this.querySelectorAll('[data-photo-cropper-window] [data-handle]'));
      // Crop state in SOURCE-CANVAS pixels (rotation-aware).
      this.cropX = 0; this.cropY = 0; this.cropW = 0; this.cropH = 0;
      this.rotation = 0;   // 0 | 90 | 180 | 270, baked into the source canvas
      this.zoom = 1;       // 1 = largest fit rect for the current aspect

      this.cropWindow.addEventListener('pointerdown', (e) => this.beginPan(e));
      this.handles.forEach(h => h.addEventListener('pointerdown', (e) => this.beginResize(e, h.dataset.handle)));
      this.naturalImage = null;  // HTMLImageElement once a valid file is loaded

      this.dropZone.addEventListener('click', () => this.fileInput.click());
      this.dropZone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.fileInput.click(); }
      });
      this.dropZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.dropZone.classList.add('is-dragover');
      });
      this.dropZone.addEventListener('dragleave', () => this.dropZone.classList.remove('is-dragover'));
      this.dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropZone.classList.remove('is-dragover');
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) this.handleFile(file);
      });
      this.fileInput.addEventListener('change', () => {
        if (this.fileInput.files && this.fileInput.files[0]) this.handleFile(this.fileInput.files[0]);
      });
    }

    // Effective source dimensions (the rotated source canvas). Falls back to the
    // natural image before the canvas is sized.
    srcW() { return (this.sourceCanvas && this.sourceCanvas.width) || (this.naturalImage ? this.naturalImage.naturalWidth : 0); }
    srcH() { return (this.sourceCanvas && this.sourceCanvas.height) || (this.naturalImage ? this.naturalImage.naturalHeight : 0); }

    handleFile(file) {
      this.hideBanner();
      if (!ACCEPTED_TYPES.includes(file.type)) {
        return this.fail('Only JPEG or PNG photos accepted. HEIC and other formats aren\'t supported.');
      }
      if (file.size > MAX_FILE_BYTES) {
        return this.fail('File too large. Maximum 20 MB.');
      }
      const reader = new FileReader();
      reader.onerror = () => this.fail('Couldn\'t read this file. Try a different photo.');
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => this.fail('Couldn\'t decode this image. Try a different photo.');
        img.onload = () => {
          // Either orientation may meet the floor; below it we accept anyway and reassure.
          const meetsFloor = (img.naturalWidth >= MIN_NATURAL_W && img.naturalHeight >= MIN_NATURAL_H) ||
                             (img.naturalWidth >= MIN_NATURAL_H && img.naturalHeight >= MIN_NATURAL_W);
          this.naturalImage = img;
          // Keep the untouched original File so the host can upload it alongside the
          // baked JPEG (for a full-resolution backend re-render).
          this.originalFile = file;
          this.rotation = 0;
          this.zoom = 1;
          this.renderImageOnCanvas();
          this.dropZone.hidden = true;
          this.cropArea.hidden = false;
          this.dispatchEvent(new CustomEvent('photo-cropper:loaded', {
            bubbles: true,
            detail: { naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight, fileSize: file.size, fileType: file.type, meetsFloor: meetsFloor }
          }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }

    renderImageOnCanvas() {
      const img = this.naturalImage;
      if (!img) return;
      // Source canvas holds the FULL-resolution image rotated by this.rotation.
      // All crop math + export read from this canvas, so rotation is baked once here.
      const rot = ((this.rotation % 360) + 360) % 360;
      const swap = (rot === 90 || rot === 270);
      const nw = img.naturalWidth, nh = img.naturalHeight;
      this.sourceCanvas.width = swap ? nh : nw;
      this.sourceCanvas.height = swap ? nw : nh;
      this.cropArea.style.position = 'relative';
      const ctx = this.sourceCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.sourceCanvas.width, this.sourceCanvas.height);
      ctx.save();
      ctx.translate(this.sourceCanvas.width / 2, this.sourceCanvas.height / 2);
      ctx.rotate(rot * Math.PI / 180);
      ctx.drawImage(img, -nw / 2, -nh / 2, nw, nh);
      ctx.restore();
      this.snapCropToAspect();
    }

    // Current display-px per source-px. Recomputed dynamically — never stored,
    // so layout reflows can't make the crop math drift from what's displayed.
    currentScale() {
      const cw = this.sourceCanvas ? this.sourceCanvas.clientWidth : 0;
      const sw = this.srcW();
      if (!cw || !sw) return 1;
      return cw / sw;
    }

    fail(msg) {
      this.banner.textContent = msg;
      this.banner.className = 'photo-cropper__banner is-error';
      this.banner.hidden = false;
      this.banner.setAttribute('role', 'alert');
      this.dispatchEvent(new CustomEvent('photo-cropper:error', { bubbles: true, detail: { message: msg } }));
    }
    hideBanner() {
      this.banner.hidden = true;
      this.banner.removeAttribute('role');
      this.banner.textContent = '';
      this.banner.className = 'photo-cropper__banner';
    }

    reset() {
      this.naturalImage = null;
      this.originalFile = null;
      // Zero the crop rect + transforms so currentEffectiveDpi() returns null in the
      // no-image state (otherwise the DPI badge / events fire over the empty drop zone).
      this.cropX = 0; this.cropY = 0; this.cropW = 0; this.cropH = 0;
      this.rotation = 0; this.zoom = 1;
      this.fileInput.value = '';
      this.cropArea.hidden = true;
      this.dropZone.hidden = false;
      this.cropWindow.hidden = true;
      if (this.dim) this.dim.hidden = true;
      this.hideBanner();
      this.hideStatus();
      this.dispatchEvent(new CustomEvent('photo-cropper:reset', { bubbles: true }));
    }

    parseAspectRatio() {
      const raw = (this.getAttribute('data-aspect-ratio') || '2/3').trim();
      const [a, b] = raw.split('/').map(Number);
      if (!a || !b) return 2 / 3;
      return a / b;
    }

    snapCropToAspect() {
      if (!this.naturalImage) return;
      const ar = this.parseAspectRatio();
      const imgW = this.srcW();
      const imgH = this.srcH();
      // Largest rect of aspect ar that fits inside imgW × imgH.
      let w = imgW, h = Math.round(imgW / ar);
      if (h > imgH) { h = imgH; w = Math.round(imgH * ar); }
      this.cropX = Math.round((imgW - w) / 2);
      this.cropY = Math.round((imgH - h) / 2);
      this.cropW = w;
      this.cropH = h;
      this.zoom = 1;
      this.renderCropWindow();
      this.dispatchCropChange();
    }

    // Zoom the crop in/out around its current center. zoom >= 1 (1 = full fit).
    setZoom(zoom) {
      if (!this.naturalImage) return;
      zoom = Math.max(1, Math.min(4, Number(zoom) || 1));
      this.zoom = zoom;
      const ar = this.parseAspectRatio();
      const W = this.srcW(), H = this.srcH();
      let fitW = W, fitH = Math.round(W / ar);
      if (fitH > H) { fitH = H; fitW = Math.round(H * ar); }
      const w = Math.max(64, Math.round(fitW / zoom));
      const h = Math.max(64, Math.round(fitH / zoom));
      const cx = this.cropX + this.cropW / 2;
      const cy = this.cropY + this.cropH / 2;
      this.cropX = Math.round(Math.max(0, Math.min(W - w, cx - w / 2)));
      this.cropY = Math.round(Math.max(0, Math.min(H - h, cy - h / 2)));
      this.cropW = w; this.cropH = h;
      this.renderCropWindow();
      this.dispatchCropChange();
    }

    // Rotate the source image 90° clockwise. Resets zoom + re-centers the crop.
    rotate90() {
      if (!this.naturalImage) return;
      this.rotation = (this.rotation + 90) % 360;
      this.zoom = 1;
      this.renderImageOnCanvas();  // re-draws rotated and re-snaps the crop
      this.dispatchEvent(new CustomEvent('photo-cropper:rotate', { bubbles: true, detail: { rotation: this.rotation } }));
    }

    renderCropWindow() {
      if (!this.naturalImage) return;
      this.cropWindow.hidden = false;
      const imgW = this.srcW();
      const imgH = this.srcH();
      // Percentages, not pixels — so display reflows don't drift the visible
      // window away from the source-pixel cropX/cropY values used by exportToFile.
      const leftPct = (this.cropX / imgW) * 100;
      const topPct  = (this.cropY / imgH) * 100;
      const wPct    = (this.cropW / imgW) * 100;
      const hPct    = (this.cropH / imgH) * 100;
      Object.assign(this.cropWindow.style, {
        left:   leftPct + '%',
        top:    topPct  + '%',
        width:  wPct    + '%',
        height: hPct    + '%',
      });
      // Update the dim overlay's clip-path to cut out the crop window's rectangle.
      if (this.dim) {
        this.dim.hidden = false;
        const r = leftPct + wPct;
        const b = topPct + hPct;
        this.dim.style.clipPath =
          'polygon(' +
          '0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ' +
          leftPct + '% ' + topPct + '%, ' +
          leftPct + '% ' + b + '%, ' +
          r + '% ' + b + '%, ' +
          r + '% ' + topPct + '%, ' +
          leftPct + '% ' + topPct + '%, ' +
          '0% 0%' +
          ')';
      }
    }

    beginPan(e) {
      if (e.target !== this.cropWindow) return;
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const origX = this.cropX;
      const origY = this.cropY;
      const imgW = this.srcW();
      const imgH = this.srcH();
      const onMove = (mv) => {
        const s = this.currentScale() || 1;
        const dx = (mv.clientX - startX) / s;
        const dy = (mv.clientY - startY) / s;
        this.cropX = Math.max(0, Math.min(imgW - this.cropW, Math.round(origX + dx)));
        this.cropY = Math.max(0, Math.min(imgH - this.cropH, Math.round(origY + dy)));
        this.renderCropWindow();
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        this.dispatchCropChange();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    beginResize(e, handle) {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const origX = this.cropX, origY = this.cropY, origW = this.cropW, origH = this.cropH;
      const ar = this.parseAspectRatio();
      const imgW = this.srcW();
      const imgH = this.srcH();
      const onMove = (mv) => {
        const s = this.currentScale() || 1;
        const dx = (mv.clientX - startX) / s;
        const dy = (mv.clientY - startY) / s;
        const widthDelta = (handle === 'tr' || handle === 'br') ? dx : -dx;
        let newW = Math.max(64, Math.round(origW + widthDelta));
        let newH = Math.round(newW / ar);
        let newX = origX, newY = origY;
        if (handle === 'tl') { newX = origX + (origW - newW); newY = origY + (origH - newH); }
        if (handle === 'tr') { newY = origY + (origH - newH); }
        if (handle === 'bl') { newX = origX + (origW - newW); }
        if (newX < 0) { newW += newX; newX = 0; newH = Math.round(newW / ar); }
        if (newY < 0) { newH += newY; newY = 0; newW = Math.round(newH * ar); }
        if (newX + newW > imgW) { newW = imgW - newX; newH = Math.round(newW / ar); }
        if (newY + newH > imgH) { newH = imgH - newY; newW = Math.round(newH * ar); }
        this.cropX = newX; this.cropY = newY; this.cropW = newW; this.cropH = newH;
        this.renderCropWindow();
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        this.dispatchCropChange();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    dispatchCropChange() {
      this.updateDpiStatus();
      this.dispatchEvent(new CustomEvent('photo-cropper:crop-change', {
        bubbles: true,
        detail: {
          cropX: this.cropX, cropY: this.cropY, cropW: this.cropW, cropH: this.cropH,
          naturalW: this.srcW(), naturalH: this.srcH(),
        }
      }));
    }

    attributeChangedCallback(name) {
      if (!this.isConnected) return;
      if (name === 'data-aspect-ratio') this.snapCropToAspect();
      if (name === 'data-print-cm-width' || name === 'data-print-cm-height' ||
          name === 'data-dpi-ok' || name === 'data-dpi-floor') this.updateDpiStatus();
    }

    currentEffectiveDpi() {
      const cmW = parseFloat(this.getAttribute('data-print-cm-width'));
      const cmH = parseFloat(this.getAttribute('data-print-cm-height'));
      if (!cmW || !cmH || !this.cropW || !this.cropH) return null;
      const printWin = cmW / 2.54;
      const printHin = cmH / 2.54;
      return Math.min(this.cropW / printWin, this.cropH / printHin);
    }

    // Uploaded-DPI thresholds for the currently selected poster size, supplied by the host.
    dpiThresholds() {
      const floor = parseFloat(this.getAttribute('data-dpi-floor'));
      const ok = parseFloat(this.getAttribute('data-dpi-ok'));
      return {
        floor: isFinite(floor) && floor > 0 ? floor : DPI_RED_BELOW,
        ok: isFinite(ok) && ok > 0 ? ok : DPI_YELLOW_BELOW
      };
    }

    updateDpiStatus() {
      const dpi = this.currentEffectiveDpi();
      if (dpi == null) {
        this.hideStatus();
        this.dispatchEvent(new CustomEvent('photo-cropper:dpi-change', { bubbles: true, detail: { dpi: null } }));
        return;
      }
      const limits = this.dpiThresholds();
      const rounded = Math.round(dpi);
      let state, text;
      if (dpi < limits.floor) {
        state = 'error';
        text = getCustomPhotoString('dpi_error', { dpi: rounded }) || `Too low for this size (${rounded} DPI)`;
      } else if (dpi < limits.ok) {
        state = 'warn';
        text = getCustomPhotoString('dpi_warning', { dpi: rounded }) || `Soft at this size (${rounded} DPI)`;
      } else {
        state = 'ok';
        text = getCustomPhotoString('dpi_excellent') || 'Excellent quality';
      }
      this.showStatus(state, text);
      this.dispatchEvent(new CustomEvent('photo-cropper:dpi-change', {
        bubbles: true,
        detail: { dpi: rounded, state }
      }));
    }

    showStatus(state, text) {
      // Never surface the quality badge unless an image is actually loaded.
      if (!this.statusEl || !this.naturalImage) return;
      this.statusEl.dataset.state = state;
      this.statusEl.hidden = false;
      this.statusEl.setAttribute('role', state === 'error' ? 'alert' : 'status');
      if (this.statusTextEl) this.statusTextEl.textContent = text;
    }

    hideStatus() {
      if (!this.statusEl) return;
      this.statusEl.hidden = true;
      this.statusEl.removeAttribute('role');
      if (this.statusTextEl) this.statusTextEl.textContent = '';
    }

    isReadyForCart() {
      const dpi = this.currentEffectiveDpi();
      return this.naturalImage != null && dpi != null && dpi >= this.dpiThresholds().floor;
    }

    // Small JPEG dataURL of the current crop (no filter/border/text) for the
    // filter strip thumbnails, which apply each CSS filter on top.
    cropThumbnailDataURL(maxEdge) {
      if (!this.naturalImage || !this.cropW || !this.cropH) return null;
      maxEdge = maxEdge || 220;
      const ar = this.cropW / this.cropH;
      let w = maxEdge, h = Math.round(maxEdge / ar);
      if (h > maxEdge) { h = maxEdge; w = Math.round(maxEdge * ar); }
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const cx = c.getContext('2d');
      cx.drawImage(this.sourceCanvas, this.cropX, this.cropY, this.cropW, this.cropH, 0, 0, w, h);
      try { return c.toDataURL('image/jpeg', 0.82); } catch (e) { return null; }
    }

    // ---- Compositing helpers --------------------------------------------------

    // Draw a source sub-rect into a dest rect with object-fit: cover (center).
    _drawCover(ctx, src, sx, sy, sw, sh, dx, dy, dw, dh) {
      if (dw <= 0 || dh <= 0) return;
      const sAR = sw / sh, dAR = dw / dh;
      let csx = sx, csy = sy, csw = sw, csh = sh;
      if (sAR > dAR) { csw = sh * dAR; csx = sx + (sw - csw) / 2; }
      else { csh = sw / dAR; csy = sy + (sh - csh) / 2; }
      ctx.drawImage(src, csx, csy, csw, csh, dx, dy, dw, dh);
    }

    _measureTracked(ctx, text, track) {
      let w = 0;
      for (const ch of text) w += ctx.measureText(ch).width + track;
      return text.length ? w - track : 0;
    }

    _fillTracked(ctx, text, centerX, y, track) {
      const total = this._measureTracked(ctx, text, track);
      let x = centerX - total / 2;
      const prevAlign = ctx.textAlign;
      ctx.textAlign = 'left';
      for (const ch of text) {
        ctx.fillText(ch, x, y);
        x += ctx.measureText(ch).width + track;
      }
      ctx.textAlign = prevAlign;
    }

    // Draw centered, uppercase, letter-spaced headline + subtitle inside a band.
    _drawCaption(ctx, o) {
      const family = FONT_FAMILIES[o.font] || FONT_FAMILIES.modern;
      const weight = FONT_WEIGHTS[o.font] || FONT_WEIGHTS.modern;
      const head = (o.headline || '').toUpperCase();
      const sub = (o.subtitle || '').toUpperCase();
      if (!head && !sub) return;

      const bandH = Math.max(1, o.bandBottom - o.bandTop);
      const trackHead = 0.14, trackSub = 0.22;

      ctx.save();
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = o.color;
      if (o.shadow) {
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = Math.round(bandH * 0.04);
        ctx.shadowOffsetY = Math.round(bandH * 0.015);
      }

      // Shrink headline to fit width.
      let headSize = Math.round(bandH * (sub ? 0.36 : 0.5));
      for (let i = 0; i < 30; i++) {
        ctx.font = weight + ' ' + headSize + 'px ' + family;
        if (this._measureTracked(ctx, head, headSize * trackHead) <= o.maxWidth * 0.9 || headSize <= 8) break;
        headSize = Math.round(headSize * 0.94);
      }
      // Shrink the subtitle independently so a long line can't run off-canvas.
      let subSize = sub ? Math.max(8, Math.round(headSize * 0.5)) : 0;
      if (sub) {
        for (let i = 0; i < 30; i++) {
          ctx.font = '400 ' + subSize + 'px ' + family;
          if (this._measureTracked(ctx, sub, subSize * trackSub) <= o.maxWidth * 0.9 || subSize <= 8) break;
          subSize = Math.round(subSize * 0.94);
        }
      }
      const lineGap = sub ? Math.round(headSize * 0.5) : 0;
      const totalH = headSize + (sub ? lineGap + subSize : 0);
      let y = o.bandTop + (bandH - totalH) / 2 + headSize;  // headline baseline

      if (head) {
        ctx.font = weight + ' ' + headSize + 'px ' + family;
        this._fillTracked(ctx, head, o.centerX, y, headSize * trackHead);
      }
      if (sub) {
        y += lineGap + subSize;
        ctx.font = '400 ' + subSize + 'px ' + family;
        this._fillTracked(ctx, sub, o.centerX, y, subSize * trackSub);
      }
      ctx.restore();
    }

    /**
     * Composite + export the print-resolution JPEG.
     * @param {HTMLInputElement} targetFileInput - file input to receive the JPEG.
     * @param {object} [opts]
     * @param {string} [opts.filter]  - filter key (see util.poster-filters.js)
     * @param {string} [opts.border]  - 'white' | 'beige' | 'black' | 'none'
     * @param {object} [opts.text]    - { headline, subtitle, font }
     * Called with no opts it produces a plain cropped JPEG (back-compatible).
     */
    async exportToFile(targetFileInput, opts) {
      if (!this.naturalImage) throw new Error('no image loaded');
      opts = opts || {};
      const filterKey = opts.filter || 'original';
      const border = opts.border || 'none';
      const text = opts.text || {};
      const headline = (text.headline || '').trim();
      const subtitle = (text.subtitle || '').trim();
      const font = text.font || 'modern';

      let targetW = parseInt(this.getAttribute('data-target-px-width') || '2480', 10);
      let targetH = parseInt(this.getAttribute('data-target-px-height') || '3508', 10);
      // Safari/iOS caps canvases at ~16 MP. Downscale proportionally if needed.
      const MAX_CANVAS_PIXELS = 16 * 1024 * 1024;
      if (targetW * targetH > MAX_CANVAS_PIXELS) {
        const factor = Math.sqrt(MAX_CANVAS_PIXELS / (targetW * targetH));
        targetW = Math.max(1, Math.round(targetW * factor));
        targetH = Math.max(1, Math.round(targetH * factor));
        console.info('[photo-cropper] downscaled export canvas to', targetW, 'x', targetH, 'to fit browser limits');
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');

      const matColor = MAT_COLORS[border] || null;
      const inset = matColor ? Math.round(Math.min(targetW, targetH) * BORDER_FRAC) : 0;
      const hasText = headline.length > 0 || subtitle.length > 0;
      const captionBand = (hasText && matColor) ? Math.round(Math.min(targetW, targetH) * CAPTION_BAND_FRAC) : 0;

      // Background mat (or leave transparent->white via JPEG for full bleed).
      if (matColor) { ctx.fillStyle = matColor; ctx.fillRect(0, 0, targetW, targetH); }

      // Photo destination rect.
      const dx = inset;
      const dy = inset;
      const dw = targetW - inset * 2;
      const dh = targetH - inset - inset - captionBand;

      // Draw the cropped (rotation-baked) source, object-fit:cover. Apply the filter
      // via manual pixel math (NOT ctx.filter, which is a no-op on older Safari/iOS and
      // would silently bake/preview an unfiltered photo). Filter the photo region only.
      this._drawCover(ctx, this.sourceCanvas, this.cropX, this.cropY, this.cropW, this.cropH, dx, dy, dw, dh);
      if (filterKey && filterKey !== 'original' && window.PosteraPosterFilters
          && window.PosteraPosterFilters.applyToImageData && dw > 0 && dh > 0) {
        try {
          const idata = ctx.getImageData(dx, dy, dw, dh);
          window.PosteraPosterFilters.applyToImageData(idata, filterKey);
          ctx.putImageData(idata, dx, dy);
        } catch (e) { console.warn('[photo-cropper] pixel filter failed', e); }
      }

      // Caption.
      if (hasText) {
        let color, bandTop, bandBottom, shadow;
        if (matColor) {
          color = (border === 'black') ? '#f2f2f2' : '#1a1a1a';
          bandTop = dy + dh;
          bandBottom = targetH - inset;
          shadow = false;
        } else {
          color = '#ffffff';
          bandBottom = targetH - Math.round(targetH * 0.05);
          bandTop = bandBottom - Math.round(targetH * 0.16);
          shadow = true;
        }
        this._drawCaption(ctx, {
          headline, subtitle, font, color, shadow,
          centerX: targetW / 2, bandTop, bandBottom, maxWidth: dw
        });
      }

      let blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.9));
      if (!blob) throw new Error('canvas.toBlob returned null');
      if (blob.size > MAX_FILE_BYTES) {
        blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.75));
        if (!blob || blob.size > MAX_FILE_BYTES) {
          this.fail('Cropped image is too large even at reduced quality. Choose a smaller size.');
          throw new Error('export exceeds 20 MB');
        }
      }
      const file = new File([blob], 'custom-photo.jpg', { type: 'image/jpeg' });
      if (!targetFileInput) {
        console.warn('[photo-cropper] no target file input passed; cropped file not attached.');
      } else if (typeof DataTransfer !== 'function') {
        console.warn('[photo-cropper] DataTransfer not supported; cropped file not attached.');
      } else {
        const dt = new DataTransfer();
        dt.items.add(file);
        try {
          targetFileInput.files = dt.files;
        } catch (assignErr) {
          console.warn('[photo-cropper] could not assign files to target input:', assignErr);
        }
      }
      // Geometry as FRACTIONS of the output, never pixels: targetW/H here may have been
      // capped to 16 MP above, while the print backend renders at the full properties[_print_px].
      // Fractions survive that rescale; pixel offsets would silently shrink the mat.
      const layout = {
        borderFrac: BORDER_FRAC,
        captionBandFrac: CAPTION_BAND_FRAC,
        mat: matColor,
        photoRect: { x: dx / targetW, y: dy / targetH, w: dw / targetW, h: dh / targetH }
      };
      const detail = {
        fileSize: blob.size,
        targetW, targetH,
        crop: { x: this.cropX, y: this.cropY, w: this.cropW, h: this.cropH },
        natural: { w: this.naturalImage.naturalWidth, h: this.naturalImage.naturalHeight },
        rotation: this.rotation,
        layout,
        filter: filterKey, border, text: { headline, subtitle, font },
        dpi: Math.round(this.currentEffectiveDpi() || 0),
      };
      this.dispatchEvent(new CustomEvent('photo-cropper:done', { bubbles: true, detail }));
      return detail;
    }
  }

  if (!customElements.get('photo-cropper')) {
    customElements.define('photo-cropper', PhotoCropper);
  }
})();
